import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface GrowthChartDataItem {
  name: string;
  year?: number;
  popGrowth: number;
  housingGrowth: number;
  prevYear?: number;
  prevPopGrowth?: number;
  prevHousingGrowth?: number;
  isSelected: boolean;
}

export const useGrowthChartData = (
  placeName: string | null,
  placeFips: string | null
) => {
  const [data, setData] = useState<GrowthChartDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!placeFips || !placeName) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [metricsResponse, housingGrowthResponse] = await Promise.all([
          supabase
            .from('ACS_BPS_metrics_demo')
            .select('year, total_population')
            .eq('place_fips', placeFips)
            .eq('Place', placeName)
            .order('year', { ascending: false })
            .limit(3),
          supabase
            .from('acs_bps_housing_stock_growth')
            .select('year, housing_stock_growth_if_permits_built_instantly')
            .eq('place_fips', placeFips)
            .eq('Place', placeName)
            .order('year', { ascending: false })
            .limit(2)
        ]);

        if (metricsResponse.error) throw metricsResponse.error;
        if (housingGrowthResponse.error) throw housingGrowthResponse.error;

        const metricsRecords = metricsResponse.data;
        const housingRecords = housingGrowthResponse.data;

        console.log('Raw metricsResponse:', metricsResponse);
        console.log('Raw housingGrowthResponse:', housingGrowthResponse);

        if (!metricsRecords || metricsRecords.length === 0 || !housingRecords || housingRecords.length === 0) {
          setData([]);
          setLoading(false);
          return;
        }

        const latestHousingRecord = housingRecords[0];
        const latestMetricRecord = metricsRecords.find(r => r.year === latestHousingRecord.year);

        if (!latestMetricRecord) {
            setData([]);
            setLoading(false);
            return;
        }

        const prevHousingRecord = housingRecords.length > 1 ? housingRecords[1] : null;
        const prevMetricRecord1 = prevHousingRecord ? metricsRecords.find(r => r.year === prevHousingRecord.year) : null;
        const prevMetricRecord2 = prevMetricRecord1 ? metricsRecords.find(r => r.year === prevMetricRecord1.year - 1) : null;
        
        let popGrowth = 0;
        if (latestMetricRecord && prevMetricRecord1 && latestMetricRecord.total_population && prevMetricRecord1.total_population) {
            const latestPop = Number(latestMetricRecord.total_population);
            const prevPop = Number(prevMetricRecord1.total_population);
            if (prevPop > 0) popGrowth = ((latestPop - prevPop) / prevPop);
        }

        let prevPopGrowth;
        if (prevMetricRecord1 && prevMetricRecord2 && prevMetricRecord1.total_population && prevMetricRecord2.total_population) {
            const prevPop1 = Number(prevMetricRecord1.total_population);
            const prevPop2 = Number(prevMetricRecord2.total_population);
            if (prevPop2 > 0) prevPopGrowth = ((prevPop1 - prevPop2) / prevPop2);
        }

        const chartDataItem: GrowthChartDataItem = {
            name: placeName,
            year: latestHousingRecord?.year,
            popGrowth: popGrowth * 100,
            housingGrowth: latestHousingRecord?.housing_stock_growth_if_permits_built_instantly ?? 0,
            prevYear: prevHousingRecord?.year,
            prevPopGrowth: prevPopGrowth !== undefined ? prevPopGrowth * 100 : undefined,
            prevHousingGrowth: prevHousingRecord ? prevHousingRecord.housing_stock_growth_if_permits_built_instantly ?? 0 : undefined,
            isSelected: true
        };

        console.log('Population vs. Housing Growth Data (from hook):', {
            thisYear: chartDataItem.year,
            thisYearPopGrowth: chartDataItem.popGrowth,
            thisYearHousingGrowth: chartDataItem.housingGrowth,
            prevYear: chartDataItem.prevYear,
            lastYearPopGrowth: chartDataItem.prevPopGrowth,
            lastYearHousingGrowth: chartDataItem.prevHousingGrowth,
        });

        setData([chartDataItem]);

      } catch (err: any) {
        console.error('Error fetching growth chart data:', err);
        setError('Failed to load growth chart data.');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [placeFips, placeName]);

  return { data, loading, error };
}; 